import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
} from 'react';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import { formatDate } from '../../util/datetime';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BadgeIcon from '@mui/icons-material/Badge';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import AssignmentIcon from '@mui/icons-material/Assignment';

import {
  activeAdminTenantId,
  canOperateTenant,
  canWriteTenant,
  useAuth,
} from '../../auth/AuthContext';
import * as tenantsApi from '../../api/tenants';
import * as invitationsApi from '../../api/invitations';
import * as npiApi from '../../api/npi';
import { extractErrorMessage } from '../../api/errors';
import { SortHeader } from '../../components/SortHeader';
import { sortRows, useSortedRows, useTableSort } from '../../util/tableSort';
import { parseEmailList } from '../../util/bulkEmails';
import { validateEmail } from '../../validation/profile';
import { RefreshButton } from '../../components/RefreshButton';
import { RecordingsDownloadDialog } from '../../components/RecordingsDownloadDialog';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useImpersonation } from '../../auth/ImpersonationContext';
import { ViewAsParticipantDialog } from '../../components/ViewAsParticipantDialog';
import { PendingInvitations } from '../../components/PendingInvitations';
import { BulkAddParticipantsDialog } from '../../components/BulkAddParticipantsDialog';
import {
  getIntakeConfig,
  getPostSurveyConfig,
  type IntakeConfig,
  type PostSurveyConfig,
} from '../../api/intake';
import { BoothKiosk } from '../../components/intake/BoothKiosk';
import { PostSurveyStation } from '../../components/intake/PostSurveyStation';
import { IntakeWizard } from '../../components/intake/IntakeWizard';
import { ParticipantIntakeDialog } from '../../components/intake/ParticipantIntakeDialog';

type ParticipantStatusKey = 'active' | 'invited' | 'disabled' | 'no_login';

/**
 * One derived status per participant, folding the two axes the data has —
 * account access (`is_active`) and onboarding (`has_email` / `has_signed_in`)
 * — into a single pill. Suspension wins over onboarding so a disabled row
 * reads as disabled regardless of how far it got. "Allow sign-in" off is not
 * "Invited": nobody was invited, and they couldn't accept if they were.
 */
function participantStatus(p: tenantsApi.Participant): {
  key: ParticipantStatusKey;
  label: string;
  color: 'success' | 'warning' | 'default';
  variant: 'filled' | 'outlined';
} {
  if (!p.is_active) {
    return { key: 'disabled', label: 'Disabled', color: 'default', variant: 'outlined' };
  }
  if (!p.has_email) {
    return { key: 'no_login', label: 'No login', color: 'default', variant: 'outlined' };
  }
  if (p.sign_in_allowed === false) {
    return { key: 'no_login', label: 'Sign-in off', color: 'default', variant: 'outlined' };
  }
  if (p.has_signed_in) {
    return { key: 'active', label: 'Signed in', color: 'success', variant: 'filled' };
  }
  return { key: 'invited', label: 'Invited', color: 'warning', variant: 'filled' };
}

/** Initials for the row avatar — first+last of the name, else the email's
 *  first letter, else a placeholder. Purely to humanise the roster. */
function initialsOf(p: tenantsApi.Participant): string {
  const name = p.full_name?.trim();
  if (name) {
    const parts = name.split(/\s+/);
    const first = parts[0]?.[0] ?? '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (first + last).toUpperCase();
  }
  if (p.has_email && p.email) return p.email[0].toUpperCase();
  return '?';
}

/** A clickable count chip — doubles as the summary bar and the status filter
 *  (clicking narrows the table to that status; the active one is filled). */
function CountChip({
  label,
  count,
  color,
  active,
  onClick,
}: {
  label: string;
  count: number;
  color?: 'success' | 'warning';
  active: boolean;
  onClick: () => void;
}) {
  return (
    <Chip
      label={`${label} · ${count}`}
      size="small"
      onClick={onClick}
      color={active ? color ?? 'primary' : 'default'}
      variant={active ? 'filled' : 'outlined'}
      sx={{ fontWeight: 700 }}
    />
  );
}

/**
 * Tenant-admin participants page.
 *
 * Lists every user in the current tenant who holds a participant
 * membership (active and suspended) and exposes the magic-link
 * invitation flow to add more. Mirrors the Platform Admins page so
 * the mental model carries over for operators who hold both roles.
 *
 * Tenant id is implicit: the route is /tenant/participants, and the
 * user's first active tenant_admin membership picks which tenant.
 * Users who admin more than one tenant get the first one until we
 * add a topbar tenant switcher.
 *
 * Per-row affordances:
 *   - Active switch: optimistic suspend / re-enable; reverts on error.
 *   - Edit (pencil): opens a small dialog to set the participant's
 *     display name. The participant edits their own contact info via
 *     /profile — admins only manage the display name so onboarded
 *     participants can be addressed before they've signed in.
 *   - Delete (trash): hard-removes the membership row in this tenant
 *     (the user record stays — they may participate elsewhere).
 */
export function TenantParticipants() {
  const { user } = useAuth();
  const tenantId = activeAdminTenantId(user);
  const canWrite = canWriteTenant(user, tenantId);
  // Nested support session: "View as participant" only when a superadmin
  // is currently acting as this tenant's admin (SUPPORT-IMPERSONATION).
  const { isImpersonating, chain } = useImpersonation();
  const impTip = chain[chain.length - 1];
  const canViewAsParticipant = isImpersonating && impTip?.role === 'tenant_admin';
  const [viewAsOpen, setViewAsOpen] = useState(false);

  const [participants, setParticipants] = useState<
    tenantsApi.Participant[] | null
  >(null);
  const [error, setError] = useState<string | null>(null);
  // Email-bypass mode (tenant.direct_participant_entry). When true the
  // page swaps the invite-by-email flow for the direct-add form.
  const [directMode, setDirectMode] = useState(false);

  // Direct-add dialog state — only used when directMode is on.
  const [addOpen, setAddOpen] = useState(false);
  const [addName, setAddName] = useState('');
  const [addTitle, setAddTitle] = useState('');
  const [addYears, setAddYears] = useState('');
  const [addEmail, setAddEmail] = useState('');
  // Defaults ON: in a no-email tenant, when staff DO give a participant an
  // email they almost always want them to reach the portal — so send the
  // set-password link by default (the box only shows once an email is
  // typed, and they can still uncheck it).
  const [addSendWelcome, setAddSendWelcome] = useState(true);
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  // NPI lookup on the direct-add form: the number itself, plus the
  // contact/address block pulled from the registry to persist on submit,
  // and a match/error message. `addLookup` is cleared if the NPI is
  // edited after a lookup so we never save stale contact data.
  const [addNpi, setAddNpi] = useState('');
  const [npiLoading, setNpiLoading] = useState(false);
  const [npiMessage, setNpiMessage] = useState<{
    severity: 'success' | 'warning' | 'error';
    text: string;
  } | null>(null);
  const [addLookup, setAddLookup] = useState<{
    phone: string | null;
    address_line1: string | null;
    address_line2: string | null;
    city: string | null;
    state_region: string | null;
    postal_code: string | null;
    country: string | null;
  } | null>(null);
  const [addCreated, setAddCreated] = useState<string | null>(null);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSentTo, setInviteSentTo] = useState<string | null>(null);
  // ROADMAP #36 cross-tenant warning — names of the OTHER tenants
  // where the invited email is already an active participant.
  const [inviteWarnElsewhere, setInviteWarnElsewhere] = useState<
    invitationsApi.OtherTenantSummary[]
  >([]);

  // Per-row toggle state. We disable just the row being flipped, not
  // the whole table, so the operator can fire several toggles in
  // quick succession.
  const [togglingId, setTogglingId] = useState<string | null>(null);
  // Per-row "resend portal link" progress + a transient result toast.
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [resendNotice, setResendNotice] = useState<{
    severity: 'success' | 'error';
    text: string;
  } | null>(null);

  // Edit dialog state — null when closed.
  const [editing, setEditing] = useState<tenantsApi.Participant | null>(null);
  const [editName, setEditName] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [editYears, setEditYears] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editSendWelcome, setEditSendWelcome] = useState(true);
  const [editSignIn, setEditSignIn] = useState(true);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Delete confirm state — null when closed.
  const [deleting, setDeleting] = useState<tenantsApi.Participant | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  // Bulk add (direct-entry mode): a sheet where only the name is required.
  const [bulkAddOpen, setBulkAddOpen] = useState(false);
  // Conference intake (CONFERENCE-INTAKE): the tenant's booth settings (null
  // until loaded, or when the caller can't read them), the booth wizard and
  // the per-participant intake record viewer.
  const [intakeConfig, setIntakeConfig] = useState<IntakeConfig | null>(null);
  const [intakeWizardOpen, setIntakeWizardOpen] = useState(false);
  // The booth kiosk (BOOTH-SELF-ENROL): a QR code participants scan to enroll
  // on their own phones; the on-laptop wizard stays as its fallback.
  const [kioskOpen, setKioskOpen] = useState(false);
  // POST-SURVEYS: the simulation station's QR code, beside the booth kiosk.
  const [postSurveyCfg, setPostSurveyCfg] = useState<PostSurveyConfig | null>(null);
  const [stationOpen, setStationOpen] = useState(false);
  const [viewingIntake, setViewingIntake] = useState<tenantsApi.Participant | null>(null);
  // Bulk-invite dialog state.
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [bulkDupesDropped, setBulkDupesDropped] = useState(0);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkResult, setBulkResult] =
    useState<invitationsApi.BulkInviteResponse | null>(null);
  const [bulkError, setBulkError] = useState<string | null>(null);
  const bulkFileInputRef = useRef<HTMLInputElement | null>(null);
  const { sort: bulkSort, sorted: sortedBulkResults } = useSortedRows(
    bulkResult?.results ?? NO_BULK_ROWS,
    BULK_RESULT_SORT,
    'email',
  );

  const bulkParsed = parseEmailList(bulkText);

  // Bumped after any invite send so the pending-invitations panel
  // (a separate fetch from the roster) refetches and shows the new row.
  const [pendingReloadKey, setPendingReloadKey] = useState(0);

  const [refreshing, setRefreshing] = useState(false);

  // Roster search + status filter (client-side over the loaded list).
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | ParticipantStatusKey>(
    'all',
  );
  // Per-row kebab menu — anchored to whichever row's ⋮ was clicked.
  const [rowMenu, setRowMenu] = useState<{
    anchor: HTMLElement;
    participant: tenantsApi.Participant;
  } | null>(null);

  const reload = async () => {
    if (!tenantId) return;
    setError(null);
    setRefreshing(true);
    try {
      // Read the enrollment mode alongside the roster so the page knows
      // whether to show the direct-add form or the invite flow.
      const [rows, tenant] = await Promise.all([
        tenantsApi.listParticipants(tenantId),
        tenantsApi.getTenant(tenantId),
      ]);
      setParticipants(rows);
      setDirectMode(tenant.direct_participant_entry);
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'failed to load participants'));
      setParticipants([]);
    } finally {
      setRefreshing(false);
    }
  };

  const toggleActive = async (p: tenantsApi.Participant) => {
    if (!tenantId || participants === null) return;
    const next = !p.is_active;
    setTogglingId(p.id);
    setError(null);
    // Optimistic flip — find and swap in the new row.
    setParticipants(
      participants.map((row) =>
        row.id === p.id ? { ...row, is_active: next } : row,
      ),
    );
    try {
      await tenantsApi.updateParticipant(tenantId, p.id, { is_active: next });
    } catch (err: unknown) {
      // Revert on failure and surface the message.
      setParticipants(
        participants.map((row) =>
          row.id === p.id ? { ...row, is_active: p.is_active } : row,
        ),
      );
      setError(extractErrorMessage(err, 'failed to update participant'));
    } finally {
      setTogglingId(null);
    }
  };

  const resendWelcome = async (p: tenantsApi.Participant) => {
    if (!tenantId) return;
    setResendingId(p.id);
    setResendNotice(null);
    try {
      const res = await tenantsApi.resendParticipantWelcome(tenantId, p.id);
      setResendNotice({
        severity: 'success',
        text: `Portal set-password link sent to ${res.email}.`,
      });
    } catch (err: unknown) {
      setResendNotice({
        severity: 'error',
        text: extractErrorMessage(err, 'failed to resend the portal link'),
      });
    } finally {
      setResendingId(null);
    }
  };

  const openEdit = (p: tenantsApi.Participant) => {
    setEditing(p);
    setEditName(p.full_name ?? '');
    setEditTitle(p.title ?? '');
    setEditYears(
      p.years_of_experience != null ? String(p.years_of_experience) : '',
    );
    // Only a real (non-placeholder) email pre-fills; an email-free
    // participant gets a blank field the admin can fill in.
    setEditEmail(p.has_email ? p.email : '');
    setEditSendWelcome(true);
    setEditSignIn(p.sign_in_allowed !== false);
    setEditError(null);
  };

  // Email edited to a new, valid address → offer to mail a set-password
  // link so the participant can sign in (the invite prompt).
  const editCurrentEmail = editing?.has_email ? editing.email : '';
  const editEmailTrimmed = editEmail.trim();
  const editEmailChanged =
    editEmailTrimmed !== '' && editEmailTrimmed !== editCurrentEmail;
  const editEmailInvalid =
    editEmailTrimmed !== '' && validateEmail(editEmail) !== null;
  const showEditWelcome = editEmailChanged && !editEmailInvalid && editSignIn;

  const submitEdit = async (e: FormEvent) => {
    e.preventDefault();
    if (!tenantId || !editing) return;
    setEditSubmitting(true);
    setEditError(null);
    try {
      // Empty string clears full_name / title server-side. years and
      // email are only sent when present/changed (the backend treats a
      // blank email as "leave unchanged", so you can't un-email here).
      const body: Parameters<typeof tenantsApi.updateParticipant>[2] = {
        full_name: editName.trim(),
        title: editTitle.trim(),
      };
      const years = editYears.trim();
      if (years !== '') body.years_of_experience = Number(years);
      const email = editEmail.trim();
      const currentEmail = editing.has_email ? editing.email : '';
      if (email !== '' && email !== currentEmail) {
        body.email = email;
        // Only meaningful on an email change; tells the backend to mail
        // the set-password link when the admin opted in.
        body.send_welcome = editSendWelcome;
      }
      if (editSignIn !== (editing.sign_in_allowed !== false)) body.sign_in_allowed = editSignIn;
      await tenantsApi.updateParticipant(tenantId, editing.id, body);
      setEditing(null);
      await reload();
    } catch (err: unknown) {
      setEditError(extractErrorMessage(err, 'failed to update participant'));
    } finally {
      setEditSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!tenantId || !deleting) return;
    setDeleteSubmitting(true);
    setError(null);
    try {
      await tenantsApi.removeParticipant(tenantId, deleting.id);
      setDeleting(null);
      await reload();
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'failed to remove participant'));
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const openBulk = () => {
    setBulkText('');
    setBulkDupesDropped(0);
    setBulkResult(null);
    setBulkError(null);
    setBulkOpen(true);
  };

  const closeBulk = () => {
    if (bulkSubmitting) return;
    setBulkOpen(false);
    if (bulkResult) {
      // After a successful submit, the roster may have updated
      // optimistically when invitees accept — but no live row yet,
      // so just bin the result on close.
      void reload();
    }
  };

  const handleBulkFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      // Append to whatever's already in the box so an operator can
      // upload + paste together (e.g. one file plus a few stragglers).
      setBulkText((prev) => (prev ? `${prev}\n${text}` : text));
    } catch {
      setBulkError('could not read file');
    } finally {
      // Reset so re-picking the same file re-fires onChange.
      if (bulkFileInputRef.current) bulkFileInputRef.current.value = '';
    }
  };

  const submitBulk = async () => {
    if (!tenantId || bulkParsed.emails.length === 0) return;
    setBulkSubmitting(true);
    setBulkError(null);
    setBulkDupesDropped(bulkParsed.duplicatesRemoved);
    try {
      const res = await invitationsApi.bulkInviteParticipants(
        tenantId,
        bulkParsed.emails,
      );
      setBulkResult(res);
      setPendingReloadKey((k) => k + 1);
    } catch (err: unknown) {
      setBulkError(extractErrorMessage(err, 'failed to send invitations'));
    } finally {
      setBulkSubmitting(false);
    }
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  // Intake settings are optional chrome: a failure (e.g. no permission)
  // just leaves the booth button hidden.
  useEffect(() => {
    if (!tenantId) return;
    let alive = true;
    setIntakeConfig(null);
    setPostSurveyCfg(null);
    getIntakeConfig(tenantId)
      .then((cfg) => {
        if (alive) setIntakeConfig(cfg);
      })
      .catch(() => undefined);
    getPostSurveyConfig(tenantId)
      .then((cfg) => {
        if (alive) setPostSurveyCfg(cfg);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [tenantId]);

  const emailError = validateEmail(inviteEmail);
  const inviteDisabled =
    inviteSubmitting || inviteEmail.trim() === '' || emailError !== null;

  // Direct-add validation: name required; email OPTIONAL but must be
  // valid if provided.
  const addEmailError =
    addEmail.trim() === '' ? null : validateEmail(addEmail);
  const addDisabled =
    addSubmitting || addName.trim() === '' || addEmailError !== null;

  const sort = useTableSort<
    | 'email'
    | 'name'
    | 'title'
    | 'years'
    | 'npi'
    | 'verified'
    | 'joined'
    | 'lastLogin'
    | 'active'
  >('joined', 'desc');
  const sortedParticipants = useMemo(() => {
    if (participants === null) return null;
    return sortRows(
      participants,
      (p) => {
        switch (sort.orderBy) {
          case 'email':
            return p.has_email ? p.email : null;
          case 'name':
            return p.full_name;
          case 'title':
            return p.title;
          case 'years':
            return p.years_of_experience;
          case 'npi':
            return p.npi;
          case 'verified':
            // Onboarding rank: No login (0) < Invited (1) < Active (2).
            return !p.has_email || p.sign_in_allowed === false ? 0 : p.has_signed_in ? 2 : 1;
          case 'joined':
            return p.joined_at;
          case 'lastLogin':
            // Never-signed-in sorts as null, which the shared comparator
            // puts last either way — "no answer" is not an early date.
            return p.last_login_at;
          case 'active':
            return p.is_active;
        }
      },
      sort.order,
    );
  }, [participants, sort.orderBy, sort.order]);

  // Status tallies for the summary/filter chips — over the FULL list so the
  // counts don't shift as the search narrows.
  const statusCounts = useMemo(() => {
    const c = { total: 0, active: 0, invited: 0, disabled: 0, no_login: 0 };
    for (const p of participants ?? []) {
      c.total += 1;
      c[participantStatus(p).key] += 1;
    }
    return c;
  }, [participants]);

  const filteredParticipants = useMemo(() => {
    if (sortedParticipants === null) return null;
    const q = search.trim().toLowerCase();
    return sortedParticipants.filter((p) => {
      if (statusFilter !== 'all' && participantStatus(p).key !== statusFilter) {
        return false;
      }
      if (q) {
        const hay = `${p.full_name ?? ''} ${p.has_email ? p.email : ''} ${
          p.title ?? ''
        }`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [sortedParticipants, search, statusFilter]);

  const addNpiValid = /^\d{10}$/.test(addNpi);

  // Digits-only, capped at 10. Editing the number invalidates any prior
  // lookup so we don't persist contact data for a different NPI.
  const updateAddNpi = (raw: string) => {
    setAddNpi(raw.replace(/\D/g, '').slice(0, 10));
    setNpiMessage(null);
    setAddLookup(null);
  };

  const handleAddNpiLookup = async () => {
    if (!addNpiValid || npiLoading) return;
    setNpiLoading(true);
    setNpiMessage(null);
    try {
      const r = await npiApi.lookupNpi(addNpi);
      if (r.full_name) setAddName(r.full_name);
      setAddLookup({
        phone: r.phone,
        address_line1: r.address_line1,
        address_line2: r.address_line2,
        city: r.city,
        state_region: r.state_region,
        postal_code: r.postal_code,
        country: r.country,
      });
      const credentialBit = r.credential ? `, ${r.credential}` : '';
      const loc = [r.city, r.state_region].filter(Boolean).join(', ');
      const locBit = loc ? ` · ${loc}` : '';
      const phoneBit = r.phone ? ` · ${r.phone}` : '';
      setNpiMessage({
        severity: 'success',
        text: `Matched ${r.full_name ?? 'record'}${credentialBit}${locBit}${phoneBit}. Contact + address will be saved.`,
      });
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      if (status === 404) {
        setNpiMessage({
          severity: 'warning',
          text: 'No matching provider in the NPI Registry.',
        });
      } else {
        setNpiMessage({
          severity: 'error',
          text: extractErrorMessage(err, 'NPI lookup failed'),
        });
      }
    } finally {
      setNpiLoading(false);
    }
  };

  const openAdd = () => {
    setAddName('');
    setAddTitle('');
    setAddYears('');
    setAddEmail('');
    setAddSendWelcome(true);
    setAddNpi('');
    setNpiMessage(null);
    setAddLookup(null);
    setAddError(null);
    setAddCreated(null);
    setAddOpen(true);
  };

  const handleAdd = async (e: FormEvent) => {
    e.preventDefault();
    if (!tenantId) return;
    setAddError(null);
    setAddCreated(null);
    setAddSubmitting(true);
    try {
      const email = addEmail.trim();
      const years = addYears.trim();
      await tenantsApi.createParticipantDirect(tenantId, {
        full_name: addName.trim(),
        title: addTitle.trim() || null,
        years_of_experience: years === '' ? null : Number(years),
        email: email === '' ? null : email,
        send_welcome: email !== '' && addSendWelcome,
        // Send the NPI only when it's a full 10 digits; the contact/
        // address block rides along only when a lookup populated it.
        npi: addNpiValid ? addNpi : null,
        phone: addLookup?.phone ?? null,
        address_line1: addLookup?.address_line1 ?? null,
        address_line2: addLookup?.address_line2 ?? null,
        city: addLookup?.city ?? null,
        state_region: addLookup?.state_region ?? null,
        postal_code: addLookup?.postal_code ?? null,
        country: addLookup?.country ?? null,
      });
      setAddCreated(addName.trim());
      await reload();
      // Brief success, then close.
      setTimeout(() => {
        setAddOpen(false);
        setAddCreated(null);
      }, 1200);
    } catch (err: unknown) {
      setAddError(extractErrorMessage(err, 'failed to add participant'));
    } finally {
      setAddSubmitting(false);
    }
  };

  const handleInvite = async (e: FormEvent) => {
    e.preventDefault();
    if (!tenantId) return;
    setInviteError(null);
    setInviteSentTo(null);
    setInviteWarnElsewhere([]);
    setInviteSubmitting(true);
    try {
      const inv = await invitationsApi.createInvitation(tenantId, {
        email: inviteEmail.trim(),
        role: 'participant',
      });
      setInviteSentTo(inviteEmail.trim());
      setInviteWarnElsewhere(inv.enrolled_elsewhere ?? []);
      setPendingReloadKey((k) => k + 1);
      setInviteEmail('');
      // Brief in-dialog success, then close and reload. When the
      // cross-tenant warning fires (ROADMAP #36) we hold the
      // dialog open longer so the operator has time to read it.
      const closeAfterMs =
        (inv.enrolled_elsewhere ?? []).length > 0 ? 4500 : 1500;
      setTimeout(() => {
        setInviteOpen(false);
        setInviteSentTo(null);
        setInviteWarnElsewhere([]);
      }, closeAfterMs);
    } catch (err: unknown) {
      setInviteError(extractErrorMessage(err, 'failed to send invitation'));
    } finally {
      setInviteSubmitting(false);
    }
  };

  if (!tenantId) {
    // Defence-in-depth — the route is RequireAuth role="tenant_admin",
    // but if the auth state is loading or somehow stale we show a
    // gentle empty state instead of a black screen.
    return (
      <Alert severity="warning">
        You don't currently hold an active tenant_admin role. If this is
        unexpected, sign out and back in to refresh your session.
      </Alert>
    );
  }

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack>
          <Typography variant="h4">Participants</Typography>
          <Typography variant="body2" color="text.secondary">
            Users with a participant membership in this tenant.
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1.5}>
          {canViewAsParticipant && (
            <Button
              variant="outlined"
              color="warning"
              startIcon={<VisibilityIcon />}
              onClick={() => setViewAsOpen(true)}
            >
              View as participant
            </Button>
          )}
          {tenantId && (
            <RecordingsDownloadDialog
              tenantId={tenantId}
              disabled={!canOperateTenant(user, tenantId)}
            />
          )}
          <RefreshButton
            onClick={() => void reload()}
            refreshing={refreshing}
          />
          {intakeConfig?.enabled && (
            <Button
              variant="contained"
              startIcon={<BadgeIcon />}
              disabled={!canWrite}
              onClick={() => setKioskOpen(true)}
            >
              Enroll at booth
            </Button>
          )}
          {intakeConfig?.enabled && postSurveyCfg?.sim_family_id && (
            <Button
              variant="outlined"
              startIcon={<QrCode2Icon />}
              disabled={!canWrite}
              onClick={() => setStationOpen(true)}
            >
              Post survey
            </Button>
          )}
          {directMode ? (
            <>
              <Button
                variant="outlined"
                startIcon={<UploadFileIcon />}
                disabled={!canWrite}
                onClick={() => setBulkAddOpen(true)}
              >
                Bulk add
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                disabled={!canWrite}
                onClick={openAdd}
              >
                Add participant
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outlined"
                startIcon={<UploadFileIcon />}
                onClick={openBulk}
                disabled={!canWrite}
              >
                Bulk invite
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                disabled={!canWrite}
                onClick={() => {
                  setInviteEmail('');
                  setInviteError(null);
                  setInviteSentTo(null);
                  setInviteOpen(true);
                }}
              >
                Invite participant
              </Button>
            </>
          )}
        </Stack>
      </Stack>

      {tenantId && canViewAsParticipant && (
        <ViewAsParticipantDialog
          open={viewAsOpen}
          onClose={() => setViewAsOpen(false)}
          tenantId={tenantId}
          tenantName={impTip?.tenantName ?? ''}
        />
      )}

      {!canWrite && (
        <Alert severity="info">
          Your admin account is <strong>read-only</strong>. You can view
          participants but can't invite, edit or remove them.
        </Alert>
      )}

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Search + summary/status filter. The count chips are both the
          at-a-glance roster breakdown and the status filter (click to
          narrow); this replaces the separate "Awaiting first sign-in"
          table that used to duplicate the Invited rows below. */}
      {participants && participants.length > 0 && (
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems={{ md: 'center' }}
          sx={{ flexWrap: 'wrap', rowGap: 1.5 }}
        >
          <TextField
            size="small"
            placeholder="Search participants…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 240, flexGrow: 1 }}
          />
          <Stack
            direction="row"
            spacing={1}
            sx={{ flexWrap: 'wrap', rowGap: 1 }}
          >
            <CountChip
              label="All"
              count={statusCounts.total}
              active={statusFilter === 'all'}
              onClick={() => setStatusFilter('all')}
            />
            <CountChip
              label="Signed in"
              count={statusCounts.active}
              color="success"
              active={statusFilter === 'active'}
              onClick={() => setStatusFilter('active')}
            />
            <CountChip
              label="Invited"
              count={statusCounts.invited}
              color="warning"
              active={statusFilter === 'invited'}
              onClick={() => setStatusFilter('invited')}
            />
            {statusCounts.disabled > 0 && (
              <CountChip
                label="Disabled"
                count={statusCounts.disabled}
                active={statusFilter === 'disabled'}
                onClick={() => setStatusFilter('disabled')}
              />
            )}
            {statusCounts.no_login > 0 && (
              <CountChip
                label="No login"
                count={statusCounts.no_login}
                active={statusFilter === 'no_login'}
                onClick={() => setStatusFilter('no_login')}
              />
            )}
          </Stack>
        </Stack>
      )}

      {participants === null ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Card>
          {participants.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                {directMode
                  ? 'No participants yet. Click Add participant to create the first one.'
                  : 'No participants yet. Click Invite participant to send the first magic-link invitation.'}
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
            <TableHead>
              <TableRow>
                {/* Identity first, email demoted to a late column — operators
                    scan by name/role, not address. ROADMAP #38: title + years
                    are nullable until the participant fills in Profile. */}
                <SortHeader label="Name" col="name" sort={sort} />
                <SortHeader label="Job title" col="title" sort={sort} />
                <SortHeader
                  label="Years exp."
                  col="years"
                  sort={sort}
                  align="right"
                />
                <SortHeader label="NPI" col="npi" sort={sort} />
                <SortHeader label="Status" col="verified" sort={sort} />
                <SortHeader label="Joined" col="joined" sort={sort} />
                <SortHeader label="Last login" col="lastLogin" sort={sort} />
                <SortHeader label="Email" col="email" sort={sort} />
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(filteredParticipants ?? []).map((p) => {
                // Stable label for aria + dialogs — an email-free
                // participant has no email to address them by.
                const who = p.has_email ? p.email : p.full_name ?? 'participant';
                const status = participantStatus(p);
                return (
                <TableRow key={p.id} hover sx={{ opacity: p.is_active ? 1 : 0.6 }}>
                  <TableCell>
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          fontSize: '0.78rem',
                          fontWeight: 700,
                          bgcolor: 'action.selected',
                          color: 'text.secondary',
                        }}
                      >
                        {initialsOf(p)}
                      </Avatar>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {p.full_name ?? '—'}
                      </Typography>
                      {p.self_enrolled && (
                        <Chip size="small" variant="outlined" label="Self-enrolled" />
                      )}
                      {p.possible_duplicate_of && (
                        <Tooltip
                          title={(() => {
                            const other = participants?.find((x) => x.id === p.possible_duplicate_of);
                            const typed = p.contact_email ? ` They typed ${p.contact_email}.` : '';
                            return `Same email or name as ${other?.full_name ?? 'another participant'} — check whether it’s the same person.${typed}`;
                          })()}
                        >
                          <Chip size="small" color="warning" label="Possible duplicate" />
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>{p.title ?? '—'}</TableCell>
                  <TableCell align="right">
                    {p.years_of_experience != null
                      ? `${p.years_of_experience} yrs`
                      : '—'}
                  </TableCell>
                  <TableCell>
                    {p.npi ? (
                      <Typography
                        variant="body2"
                        component="span"
                        sx={{ fontFamily: 'monospace' }}
                      >
                        {p.npi}
                      </Typography>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={status.label}
                      size="small"
                      color={status.color}
                      variant={status.variant}
                    />
                  </TableCell>
                  <TableCell>
                    {formatDate(p.joined_at)}
                  </TableCell>
                  {/* Never signed in prints a dash, not a date. The Status
                      chip beside it already says "Invited"; inventing a day
                      here would contradict it. */}
                  <TableCell>
                    {p.last_login_at ? (
                      formatDate(p.last_login_at)
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        component="span"
                      >
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {p.has_email ? (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        component="span"
                      >
                        {p.email}
                      </Typography>
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.disabled"
                        component="span"
                      >
                        no email
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      aria-label={`Actions for ${who}`}
                      onClick={(e) =>
                        setRowMenu({ anchor: e.currentTarget, participant: p })
                      }
                    >
                      {togglingId === p.id || resendingId === p.id ? (
                        <CircularProgress size={18} />
                      ) : (
                        <MoreVertIcon fontSize="small" />
                      )}
                    </IconButton>
                  </TableCell>
                </TableRow>
                );
              })}
              {filteredParticipants && filteredParticipants.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary" component="span">
                      No participants match.{' '}
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => {
                        setSearch('');
                        setStatusFilter('all');
                      }}
                    >
                      Clear filters
                    </Button>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
            </TableContainer>
          )}

          {/* Invited-but-not-joined participants — embedded into this card.
              Renders nothing when there are none (e.g. direct-add tenants). */}
          <PendingInvitations
            embedded
            source={{
          list: () =>
            invitationsApi
              .listInvitations(tenantId)
              .then((r) => r.filter((i) => i.role === 'participant')),
          resend: (inv) =>
            invitationsApi
              .createInvitation(tenantId, {
                email: inv.email,
                role: inv.role,
                read_only: inv.read_only,
              })
              .then(() => undefined),
          cancel: (inv) => invitationsApi.revokeInvitation(tenantId, inv.id),
        }}
        canWrite={canWrite}
        reloadKey={pendingReloadKey}
      />

      {/* PendingInvitations (above) lists outstanding INVITATIONS — emails
          invited that have no participant row yet — so it stays. The old
          "Awaiting first sign-in" table was removed: it only duplicated the
          main table's "Invited" participants, now reachable via the Invited
          status-filter chip. */}
        </Card>
      )}

      {/* Per-row actions, consolidated into one kebab (was three inline
          icons + the Active toggle). Enable/Disable moved in here so the
          old, ambiguous "Active" switch is gone. */}
      <Menu
        anchorEl={rowMenu?.anchor ?? null}
        open={Boolean(rowMenu)}
        onClose={() => setRowMenu(null)}
      >
        {rowMenu
          ? [
              <MenuItem
                key="edit"
                disabled={!canWrite}
                onClick={() => {
                  openEdit(rowMenu.participant);
                  setRowMenu(null);
                }}
              >
                <ListItemIcon>
                  <EditIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Edit</ListItemText>
              </MenuItem>,
              rowMenu.participant.has_email ? (
                <MenuItem
                  key="resend"
                  disabled={
                    !canWrite ||
                    resendingId === rowMenu.participant.id ||
                    rowMenu.participant.sign_in_allowed === false
                  }
                  onClick={() => {
                    void resendWelcome(rowMenu.participant);
                    setRowMenu(null);
                  }}
                >
                  <ListItemIcon>
                    <ForwardToInboxIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Resend portal link</ListItemText>
                </MenuItem>
              ) : null,
              intakeConfig ? (
                <MenuItem
                  key="intake"
                  onClick={() => {
                    setViewingIntake(rowMenu.participant);
                    setRowMenu(null);
                  }}
                >
                  <ListItemIcon>
                    <AssignmentIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>View intake</ListItemText>
                </MenuItem>
              ) : null,
              <MenuItem
                key="toggle"
                disabled={!canWrite || togglingId === rowMenu.participant.id}
                onClick={() => {
                  void toggleActive(rowMenu.participant);
                  setRowMenu(null);
                }}
              >
                <ListItemIcon>
                  {rowMenu.participant.is_active ? (
                    <BlockIcon fontSize="small" />
                  ) : (
                    <CheckCircleOutlineIcon
                      fontSize="small"
                      sx={{ color: 'success.main' }}
                    />
                  )}
                </ListItemIcon>
                <ListItemText>
                  {rowMenu.participant.is_active
                    ? 'Disable access'
                    : 'Re-enable access'}
                </ListItemText>
              </MenuItem>,
              <Divider key="div" />,
              <MenuItem
                key="delete"
                disabled={!canWrite}
                onClick={() => {
                  setDeleting(rowMenu.participant);
                  setRowMenu(null);
                }}
              >
                <ListItemIcon>
                  <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
                </ListItemIcon>
                <ListItemText sx={{ color: 'error.main' }}>
                  Remove from tenant
                </ListItemText>
              </MenuItem>,
            ].filter(Boolean)
          : null}
      </Menu>

      <Dialog
        open={addOpen}
        onClose={() => (addSubmitting ? null : setAddOpen(false))}
        fullWidth
        maxWidth="sm"
      >
        <form onSubmit={handleAdd}>
          <DialogTitle>Add a participant</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Create a participant directly — no email invitation. Email is
              optional: leave it blank for a demo/session subject who won't
              log in. They'll appear in the capture-app picker right away.
            </DialogContentText>
            <Stack spacing={2}>
              <TextField
                autoFocus
                label="Full name"
                fullWidth
                required
                value={addName}
                onChange={(e) => setAddName(e.target.value)}
                disabled={addSubmitting}
              />
              <TextField
                label="Job title"
                fullWidth
                value={addTitle}
                onChange={(e) => setAddTitle(e.target.value)}
                disabled={addSubmitting}
                helperText="Optional."
              />
              <TextField
                label="Years of experience"
                type="number"
                fullWidth
                value={addYears}
                onChange={(e) => setAddYears(e.target.value)}
                disabled={addSubmitting}
                inputProps={{ min: 0, max: 100 }}
                helperText="Optional."
              />
              <Box>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <TextField
                    label="NPI (optional)"
                    fullWidth
                    value={addNpi}
                    onChange={(e) => updateAddNpi(e.target.value)}
                    disabled={addSubmitting}
                    inputProps={{ inputMode: 'numeric' }}
                    helperText={
                      addNpi.length === 0
                        ? 'US clinicians: look up to auto-fill name, contact & address.'
                        : `${addNpi.length}/10 digits`
                    }
                  />
                  <Button
                    variant="outlined"
                    onClick={handleAddNpiLookup}
                    disabled={!addNpiValid || npiLoading || addSubmitting}
                    sx={{ mt: 1, whiteSpace: 'nowrap' }}
                  >
                    {npiLoading ? <CircularProgress size={20} /> : 'Look up'}
                  </Button>
                </Stack>
                {npiMessage && (
                  <Alert severity={npiMessage.severity} sx={{ mt: 1 }}>
                    {npiMessage.text}
                  </Alert>
                )}
              </Box>
              <TextField
                label="Email (optional)"
                type="email"
                fullWidth
                value={addEmail}
                onChange={(e) => setAddEmail(e.target.value)}
                error={addEmail.trim() !== '' && addEmailError !== null}
                helperText={
                  addEmail.trim() !== '' && addEmailError
                    ? addEmailError
                    : 'Leave blank for a participant who never signs in.'
                }
                disabled={addSubmitting}
              />
              {addEmail.trim() !== '' && addEmailError === null && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={addSendWelcome}
                      onChange={(e) => setAddSendWelcome(e.target.checked)}
                      disabled={addSubmitting}
                    />
                  }
                  label="Email them a set-password link so they can sign in"
                />
              )}
            </Stack>
            {addError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {addError}
              </Alert>
            )}
            {addCreated && !addError && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Added <strong>{addCreated}</strong>.
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setAddOpen(false)}
              disabled={addSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={addDisabled}>
              {addSubmitting ? 'Adding…' : 'Add participant'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={inviteOpen}
        onClose={() => (inviteSubmitting ? null : setInviteOpen(false))}
        fullWidth
        maxWidth="sm"
      >
        <form onSubmit={handleInvite}>
          <DialogTitle>Invite a participant</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              The recipient will receive a magic-link email asking them to
              join this tenant as a participant. If they don't already have a
              10NEWTONS account, the link lets them set a password in one
              click — no separate signup step.
            </DialogContentText>
            <TextField
              autoFocus
              label="Email"
              type="email"
              fullWidth
              required
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              error={inviteEmail.trim() !== '' && emailError !== null}
              helperText={
                inviteEmail.trim() !== '' && emailError
                  ? emailError
                  : "They'll get a participant membership in this tenant."
              }
              disabled={inviteSubmitting}
            />
            {inviteError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {inviteError}
              </Alert>
            )}
            {inviteSentTo && !inviteError && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Invitation sent to <strong>{inviteSentTo}</strong>. The
                link expires in 7 days.
              </Alert>
            )}
            {inviteSentTo && inviteWarnElsewhere.length > 0 && (
              <Alert severity="info" sx={{ mt: 1 }}>
                Note: <strong>{inviteSentTo}</strong> is already a
                participant at{' '}
                <strong>
                  {inviteWarnElsewhere.map((t) => t.tenant_name).join(', ')}
                </strong>
                . The invitation still ships — coordinate with the
                other organisation's admin if you need to.
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setInviteOpen(false)}
              disabled={inviteSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={inviteDisabled}
            >
              {inviteSubmitting ? 'Sending…' : 'Send invitation'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={editing !== null}
        onClose={() => (editSubmitting ? null : setEditing(null))}
        fullWidth
        maxWidth="sm"
      >
        <form onSubmit={submitEdit}>
          <DialogTitle>Edit participant</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Edit this participant's name, job title, years of experience,
              and email. They control their own phone, address, and other
              contact details from their profile page.
            </DialogContentText>
            <TextField
              autoFocus
              label="Full name"
              fullWidth
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              disabled={editSubmitting}
              helperText="Leave blank to clear the stored name."
              sx={{ mb: 2 }}
            />
            <TextField
              label="Job title"
              fullWidth
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              disabled={editSubmitting}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Years of experience"
              type="number"
              fullWidth
              value={editYears}
              onChange={(e) => setEditYears(e.target.value)}
              disabled={editSubmitting}
              inputProps={{ min: 0, max: 100 }}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Email"
              fullWidth
              value={editEmail}
              onChange={(e) => setEditEmail(e.target.value)}
              disabled={editSubmitting}
              error={editEmailInvalid}
              helperText={
                editEmailInvalid
                  ? (validateEmail(editEmail) ?? 'enter a valid email')
                  : editing?.has_email
                    ? 'Changing this changes their sign-in identity.'
                    : 'Add an email so this participant can sign in.'
              }
            />
            {showEditWelcome && (
              <FormControlLabel
                sx={{ mt: 1 }}
                control={
                  <Checkbox
                    checked={editSendWelcome}
                    onChange={(e) => setEditSendWelcome(e.target.checked)}
                    disabled={editSubmitting}
                  />
                }
                label="Email them a set-password link so they can sign in"
              />
            )}
            <FormControlLabel
              sx={{ mt: 1, display: 'flex' }}
              control={
                <Switch
                  checked={editSignIn}
                  onChange={(e) => setEditSignIn(e.target.checked)}
                  disabled={editSubmitting}
                />
              }
              label="Allow sign-in"
            />
            <Typography variant="caption" color="text.secondary" component="p">
              {editSignIn
                ? editing?.sign_in_allowed === false && !editing.has_signed_in
                  ? 'Saving emails them a link to set a password and sign in.'
                  : 'They can sign in to see their results.'
                : 'They can’t sign in, reset a password or use an invitation link, and get no “results ready” emails. Their recordings are kept.'}
            </Typography>
            {editError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {editError}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditing(null)} disabled={editSubmitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={editSubmitting || editEmailInvalid}
            >
              {editSubmitting ? 'Saving…' : 'Save'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={bulkOpen}
        onClose={closeBulk}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Bulk invite participants</DialogTitle>
        <DialogContent>
          {!bulkResult && (
            <>
              <DialogContentText sx={{ mb: 2 }}>
                Paste a list of email addresses or upload a CSV/text
                file. One address per line works; commas and
                semicolons are fine too. We'll send a magic-link
                invitation to every valid address; duplicates and
                people who are already participants in your tenant
                are skipped with a per-row reason. People without an email
                can be added in bulk once direct participant entry is on
                (Settings → Tenant).
              </DialogContentText>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<UploadFileIcon />}
                  onClick={() => bulkFileInputRef.current?.click()}
                  disabled={bulkSubmitting}
                >
                  Choose file
                </Button>
                <input
                  ref={bulkFileInputRef}
                  type="file"
                  accept=".csv,.txt,text/*"
                  onChange={handleBulkFile}
                  hidden
                  data-testid="bulk-file-input"
                />
                <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                  Or paste addresses below.
                </Typography>
              </Stack>
              <TextField
                label="Email addresses"
                placeholder={'a@example.com\nb@example.com'}
                fullWidth
                multiline
                minRows={6}
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
                disabled={bulkSubmitting}
                helperText={
                  bulkParsed.emails.length > 0
                    ? `${bulkParsed.emails.length} unique address${bulkParsed.emails.length === 1 ? '' : 'es'}` +
                      (bulkParsed.duplicatesRemoved > 0
                        ? ` (${bulkParsed.duplicatesRemoved} duplicate${bulkParsed.duplicatesRemoved === 1 ? '' : 's'} removed)`
                        : '')
                    : 'No addresses parsed yet.'
                }
              />
              {bulkError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {bulkError}
                </Alert>
              )}
            </>
          )}
          {bulkResult && (
            <>
              <Alert
                severity={bulkResult.skipped === 0 ? 'success' : 'info'}
                sx={{ mb: 2 }}
              >
                Sent {bulkResult.sent} invitation
                {bulkResult.sent === 1 ? '' : 's'}; skipped{' '}
                {bulkResult.skipped + bulkDupesDropped}.
              </Alert>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <SortHeader label="Email" col="email" sort={bulkSort} />
                      <SortHeader label="Result" col="result" sort={bulkSort} />
                      <SortHeader label="Also at" col="alsoAt" sort={bulkSort} />
                      <SortHeader label="Detail" col="detail" sort={bulkSort} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedBulkResults.map((r, i) => (
                      <TableRow key={`${r.email}-${i}`}>
                        <TableCell>{r.email}</TableCell>
                        <TableCell>
                          <BulkStatusChip status={r.status} />
                        </TableCell>
                        <TableCell>
                          {r.enrolled_elsewhere &&
                          r.enrolled_elsewhere.length > 0 ? (
                            <Stack
                              direction="row"
                              spacing={0.5}
                              useFlexGap
                              flexWrap="wrap"
                            >
                              {r.enrolled_elsewhere.map((t) => (
                                <Chip
                                  key={t.tenant_id}
                                  label={t.tenant_name}
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                  title={`Already a participant at ${t.tenant_name}`}
                                />
                              ))}
                            </Stack>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell>{r.detail ?? '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeBulk} disabled={bulkSubmitting}>
            {bulkResult ? 'Close' : 'Cancel'}
          </Button>
          {!bulkResult && (
            <Button
              variant="contained"
              onClick={() => void submitBulk()}
              disabled={bulkSubmitting || bulkParsed.emails.length === 0}
            >
              {bulkSubmitting
                ? 'Sending…'
                : `Send ${bulkParsed.emails.length || ''} invitation${
                    bulkParsed.emails.length === 1 ? '' : 's'
                  }`.trim()}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      {tenantId && (
        <BulkAddParticipantsDialog
          tenantId={tenantId}
          open={bulkAddOpen}
          onClose={() => setBulkAddOpen(false)}
          onAdded={() => void reload()}
        />
      )}
      {tenantId && intakeConfig?.enabled && postSurveyCfg?.sim_family_id && (
        <PostSurveyStation
          open={stationOpen}
          tenantId={tenantId}
          onClose={() => setStationOpen(false)}
        />
      )}
      {tenantId && intakeConfig?.enabled && (
        <BoothKiosk
          open={kioskOpen}
          tenantId={tenantId}
          onClose={() => setKioskOpen(false)}
          onFillInHere={() => setIntakeWizardOpen(true)}
          onActivity={() => void reload()}
        />
      )}
      {tenantId && intakeConfig?.enabled && (
        <IntakeWizard
          open={intakeWizardOpen}
          tenantId={tenantId}
          config={intakeConfig}
          onClose={() => setIntakeWizardOpen(false)}
          onEnrolled={() => void reload()}
          participantName={(id) => {
            const p = participants?.find((x) => x.id === id);
            return p ? p.full_name ?? (p.has_email ? p.email : null) : null;
          }}
        />
      )}
      {tenantId && viewingIntake && (
        <ParticipantIntakeDialog
          open
          tenantId={tenantId}
          participantUserId={viewingIntake.id}
          participantName={
            viewingIntake.full_name ??
            (viewingIntake.has_email ? viewingIntake.email : '')
          }
          onClose={() => setViewingIntake(null)}
        />
      )}

      <Dialog
        open={deleting !== null}
        onClose={() => (deleteSubmitting ? null : setDeleting(null))}
      >
        <DialogTitle>
          Remove{' '}
          {deleting?.has_email
            ? deleting?.email
            : deleting?.full_name ?? 'this participant'}
          ?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This removes this participant's membership in your tenant.
            Their 10NEWTONS user account stays — they may be a
            participant elsewhere — but they immediately lose access
            here. Re-inviting the same email later creates a fresh
            membership.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)} disabled={deleteSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={() => void confirmDelete()}
            color="error"
            variant="contained"
            disabled={deleteSubmitting}
          >
            {deleteSubmitting ? 'Removing…' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={resendNotice !== null}
        autoHideDuration={5000}
        onClose={() => setResendNotice(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {resendNotice ? (
          <Alert
            severity={resendNotice.severity}
            onClose={() => setResendNotice(null)}
            variant="filled"
          >
            {resendNotice.text}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Stack>
  );
}

const BULK_STATUS: Record<
  string,
  { label: string; color?: 'success' | 'warning' | 'error' }
> = {
  sent: { label: 'Sent', color: 'success' },
  already_member: { label: 'Already a member', color: 'warning' },
  invalid_format: { label: 'Invalid email', color: 'error' },
  duplicate_in_list: { label: 'Duplicate' },
  license_limit: { label: 'License limit', color: 'error' },
};

const NO_BULK_ROWS: invitationsApi.BulkInviteRowResult[] = [];
const BULK_RESULT_SORT = {
  email: (r: invitationsApi.BulkInviteRowResult) => r.email,
  // By the label the chip shows, not the raw backend code.
  result: (r: invitationsApi.BulkInviteRowResult) =>
    BULK_STATUS[r.status]?.label ?? r.status,
  alsoAt: (r: invitationsApi.BulkInviteRowResult) =>
    (r.enrolled_elsewhere ?? []).map((t) => t.tenant_name).join(', ') || null,
  detail: (r: invitationsApi.BulkInviteRowResult) => r.detail ?? null,
};

function BulkStatusChip({ status }: { status: string }) {
  // Map backend status strings to a Chip color + label. Unknown
  // statuses get a neutral default rather than crashing — the server
  // is the source of truth and may add new categories before us.
  const known = BULK_STATUS[status];
  return (
    <Chip label={known?.label ?? status} size="small" color={known?.color ?? 'default'} />
  );
}
